import { useState, useEffect, useCallback } from "react";
import { useParams, useLocation, Link } from "wouter";
import { format } from "date-fns";
import { useAuth } from "@/lib/authContext";
import { api, Account, Invoice, InvoiceLine } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { useConfirm } from "@/lib/confirmContext";
import { ArrowLeft, Plus, Trash2, Save, Send, Loader2, Download, DollarSign, History } from "lucide-react";

const statusColors: Record<string, string> = {
  draft: "bg-gray-100 text-gray-800 border-gray-300",
  sent: "bg-blue-100 text-blue-800 border-blue-300",
  paid: "bg-green-100 text-green-800 border-green-300",
  void: "bg-red-100 text-red-800 border-red-300",
};

export default function InvoiceEdit() {
  const { user } = useAuth();
  const { toast } = useToast();
  const askConfirm = useConfirm();
  const params = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  
  const [invoice, setInvoice] = useState<(Invoice & { lines: InvoiceLine[] }) | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddLine, setShowAddLine] = useState(false);
  const [newLine, setNewLine] = useState({ description: "", quantity: "1", rate: "0" });
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    invoice_number: "",
    invoice_date: "",
    due_date: "",
    notes: "",
    account_id: "",
  });
  const [showMarkPaidDialog, setShowMarkPaidDialog] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    paid_date: new Date().toISOString().split("T")[0],
    payment_method: "check",
    payment_reference: "",
  });

  const loadData = useCallback(async () => {
    try {
      const [invoiceData, accountsData] = await Promise.all([
        api.getInvoice(params.id!),
        api.getAccounts(),
      ]);
      setInvoice(invoiceData);
      setAccounts(accountsData);
      setEditForm({
        invoice_number: invoiceData.invoice_number || "",
        invoice_date: invoiceData.invoice_date,
        due_date: invoiceData.due_date || "",
        notes: invoiceData.notes || "",
        account_id: invoiceData.account_id,
      });
    } catch (error: any) {
      toast({ title: "Error loading invoice", description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [params.id, toast]);

  useEffect(() => {
    if (user && ["manager", "admin"].includes(user.role)) {
      loadData();
    }
  }, [user, loadData]);

  const toNumber = (val: string | number | null | undefined): number => {
    if (val === null || val === undefined) return 0;
    const num = typeof val === "string" ? parseFloat(val) : val;
    return isNaN(num) ? 0 : num;
  };

  const formatCurrency = (amount: string | number | null | undefined) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(toNumber(amount));
  };

  const handleSaveDetails = async () => {
    if (!invoice) return;
    
    setSaving(true);
    try {
      const updated = await api.updateInvoice(invoice.id, {
        invoice_number: editForm.invoice_number || undefined,
        invoice_date: editForm.invoice_date,
        due_date: editForm.due_date || undefined,
        notes: editForm.notes || undefined,
        account_id: editForm.account_id,
      });
      setInvoice({ ...invoice, ...updated });
      setEditMode(false);
      toast({ title: "Invoice updated" });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleAddLine = async () => {
    if (!invoice || !newLine.description) return;
    
    setSaving(true);
    try {
      await api.addInvoiceLine(invoice.id, {
        description: newLine.description,
        quantity: parseFloat(newLine.quantity) || 1,
        rate: parseFloat(newLine.rate) || 0,
      });
      // Refetch invoice to get server-calculated totals
      await loadData();
      setNewLine({ description: "", quantity: "1", rate: "0" });
      setShowAddLine(false);
      toast({ title: "Line item added" });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLine = async (lineId: string) => {
    if (!invoice) return;
    if (!(await askConfirm({
      title: "Delete this line item?",
      subject: "line item",
      body: "will be deleted from the invoice.",
      confirmLabel: "Delete",
      destructive: true,
    }))) return;
    
    try {
      await api.deleteInvoiceLine(lineId);
      // Refetch invoice to get server-calculated totals
      await loadData();
      toast({ title: "Line item deleted" });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleMarkAsSent = async () => {
    if (!invoice) return;
    
    setSaving(true);
    try {
      const updated = await api.updateInvoice(invoice.id, { status: "sent" });
      setInvoice({ ...invoice, ...updated });
      toast({ title: "Invoice marked as sent" });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleMarkAsPaidWithDetails = async () => {
    if (!invoice) return;
    
    setSaving(true);
    try {
      const updated = await api.updateInvoice(invoice.id, { 
        status: "paid",
        paid_date: paymentForm.paid_date,
        payment_method: paymentForm.payment_method,
        payment_reference: paymentForm.payment_reference || undefined,
      });
      setInvoice({ ...invoice, ...updated });
      setShowMarkPaidDialog(false);
      toast({ title: "Invoice marked as paid" });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const [pdfLoading, setPdfLoading] = useState(false);

  const handleDownloadPDF = async () => {
    if (!invoice) return;
    setPdfLoading(true);
    try {
      const { getSupabase } = await import("@/lib/supabase");
      const sb = await getSupabase();
      const { data: { session } } = await sb.auth.getSession();
      const headers: HeadersInit = session?.access_token
        ? { Authorization: `Bearer ${session.access_token}` }
        : {};
      const res = await fetch(`/api/invoices/${invoice.id}/pdf`, { headers });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Failed to generate PDF" }));
        toast({ title: "Error", description: err.message, variant: "destructive" });
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${invoice.invoice_number || invoice.id.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast({ title: "PDF downloaded" });
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to download PDF", variant: "destructive" });
    } finally {
      setPdfLoading(false);
    }
  };

  if (!user || !["manager", "admin"].includes(user.role)) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Access denied. Manager or admin role required.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Invoice not found</p>
        <Link href="/invoices">
          <Button variant="link" className="p-0 mt-2">Back to Invoices</Button>
        </Link>
      </div>
    );
  }

  const isDraft = invoice.status === "draft";
  const isAdmin = user?.role === "admin";
  const canEdit = isDraft || isAdmin;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link href="/invoices">
            <Button variant="ghost" size="icon" data-testid="button-back">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground" data-testid="text-invoice-title">
                Invoice {invoice.invoice_number || "(No Number)"}
              </h1>
              <Badge 
                variant="outline" 
                className={statusColors[invoice.status]}
              >
                {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
              </Badge>
            </div>
            <p className="text-muted-foreground">{invoice.accounts?.account_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          <Button variant="outline" onClick={handleDownloadPDF} disabled={pdfLoading} data-testid="button-save-pdf">
            {pdfLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Download PDF
          </Button>
          {isDraft && (
            <Button onClick={handleMarkAsSent} disabled={saving} variant="outline" data-testid="button-mark-sent">
              <Send className="w-4 h-4 mr-2" />
              Mark as Sent
            </Button>
          )}
          {invoice.status === "sent" && (
            <Button onClick={() => setShowMarkPaidDialog(true)} disabled={saving} data-testid="button-mark-paid">
              <DollarSign className="w-4 h-4 mr-2" />
              Mark as Paid
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <Card className="md:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle>Invoice Details</CardTitle>
            {canEdit && !editMode && (
              <Button variant="outline" size="sm" onClick={() => setEditMode(true)} data-testid="button-edit-details">
                Edit
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {editMode ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Client</Label>
                    <Select 
                      value={editForm.account_id} 
                      onValueChange={(v) => setEditForm({ ...editForm, account_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {accounts.map((a) => (
                          <SelectItem key={a.id} value={a.id}>{a.account_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Invoice Number</Label>
                    <Input
                      value={editForm.invoice_number}
                      onChange={(e) => setEditForm({ ...editForm, invoice_number: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Invoice Date</Label>
                    <Input
                      type="date"
                      value={editForm.invoice_date}
                      onChange={(e) => setEditForm({ ...editForm, invoice_date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Due Date</Label>
                    <Input
                      type="date"
                      value={editForm.due_date}
                      onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
                  <Button onClick={handleSaveDetails} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Invoice Date</span>
                  <span>{format(new Date(invoice.invoice_date), "MMMM d, yyyy")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Due Date</span>
                  <span>{invoice.due_date ? format(new Date(invoice.due_date), "MMMM d, yyyy") : "-"}</span>
                </div>
                {invoice.notes && (
                  <div className="pt-2">
                    <span className="text-muted-foreground block mb-1">Notes</span>
                    <p className="text-foreground">{invoice.notes}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span>{formatCurrency(invoice.subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Tax</span>
              <span>{formatCurrency(invoice.tax)}</span>
            </div>
            <Separator />
            <div className="flex justify-between font-bold">
              <span>Total</span>
              <span className="text-lg">{formatCurrency(invoice.total)}</span>
            </div>
            {invoice.status === "paid" && (invoice.paid_date || invoice.payment_method) && (
              <>
                <Separator />
                <div className="space-y-2 pt-1">
                  <div className="text-sm font-medium text-green-600">Payment Details</div>
                  {invoice.paid_date && !isNaN(new Date(invoice.paid_date).getTime()) && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Paid Date</span>
                      <span>{format(new Date(invoice.paid_date), "MMMM d, yyyy")}</span>
                    </div>
                  )}
                  {invoice.payment_method && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Method</span>
                      <span className="capitalize">{invoice.payment_method.replace("_", " ")}</span>
                    </div>
                  )}
                  {invoice.payment_reference && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{invoice.payment_method === "check" ? "Check #" : "Reference"}</span>
                      <span>{invoice.payment_reference}</span>
                    </div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div>
            <CardTitle>Line Items</CardTitle>
            <CardDescription>{invoice.lines.length} item(s)</CardDescription>
          </div>
          {isDraft && (
            <Button onClick={() => setShowAddLine(true)} data-testid="button-add-line">
              <Plus className="w-4 h-4 mr-2" />
              Add Item
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {invoice.lines.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              No line items yet. {isDraft && "Click 'Add Item' to add one."}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50%]">Description</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  {isDraft && <TableHead className="w-10"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoice.lines.map((line) => (
                  <TableRow key={line.id} data-testid={`row-line-${line.id}`}>
                    <TableCell>{line.description}</TableCell>
                    <TableCell className="text-right">{toNumber(line.quantity).toFixed(2)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(line.rate)}</TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(line.amount)}</TableCell>
                    {isDraft && (
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDeleteLine(line.id)}
                          data-testid={`button-delete-line-${line.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {invoice.status_log && invoice.status_log.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <History className="w-4 h-4" />
              Status History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {[...invoice.status_log].reverse().map((entry, idx) => (
                <div key={idx} className="flex items-center gap-3 text-sm py-1.5 border-b last:border-0">
                  <Badge variant="outline" className={statusColors[entry.status] || ""}>
                    {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                  </Badge>
                  <span className="text-muted-foreground">
                    {format(new Date(entry.changed_at), "MMM d, yyyy 'at' h:mm a")}
                  </span>
                  <span className="text-muted-foreground">by {entry.changed_by_name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={showAddLine} onOpenChange={setShowAddLine}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Line Item</DialogTitle>
            <DialogDescription>Add a new item to this invoice.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Description *</Label>
              <Input
                value={newLine.description}
                onChange={(e) => setNewLine({ ...newLine, description: e.target.value })}
                placeholder="e.g. Consulting services"
                data-testid="input-line-description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newLine.quantity}
                  onChange={(e) => setNewLine({ ...newLine, quantity: e.target.value })}
                  data-testid="input-line-quantity"
                />
              </div>
              <div className="space-y-2">
                <Label>Rate ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newLine.rate}
                  onChange={(e) => setNewLine({ ...newLine, rate: e.target.value })}
                  data-testid="input-line-rate"
                />
              </div>
            </div>
            <div className="text-right text-sm text-muted-foreground">
              Amount: {formatCurrency((parseFloat(newLine.quantity) || 0) * (parseFloat(newLine.rate) || 0))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddLine(false)}>Cancel</Button>
            <Button 
              onClick={handleAddLine} 
              disabled={saving || !newLine.description}
              data-testid="button-add-line-submit"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Add Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showMarkPaidDialog} onOpenChange={setShowMarkPaidDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Mark Invoice as Paid</DialogTitle>
            <DialogDescription>Enter payment details for this invoice.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Date Paid *</Label>
              <Input
                type="date"
                value={paymentForm.paid_date}
                onChange={(e) => setPaymentForm({ ...paymentForm, paid_date: e.target.value })}
                data-testid="input-paid-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Payment Method *</Label>
              <Select 
                value={paymentForm.payment_method} 
                onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_method: v })}
              >
                <SelectTrigger data-testid="select-payment-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="credit_card">Credit Card</SelectItem>
                  <SelectItem value="ach">ACH/Bank Transfer</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{paymentForm.payment_method === "check" ? "Check Number" : "Reference / Transaction ID"}</Label>
              <Input
                value={paymentForm.payment_reference}
                onChange={(e) => setPaymentForm({ ...paymentForm, payment_reference: e.target.value })}
                placeholder={paymentForm.payment_method === "check" ? "e.g. 1234" : "e.g. TXN-12345"}
                data-testid="input-payment-reference"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMarkPaidDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleMarkAsPaidWithDetails} 
              disabled={saving || !paymentForm.paid_date || !paymentForm.payment_method}
              data-testid="button-confirm-mark-paid"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <DollarSign className="w-4 h-4 mr-2" />}
              Mark as Paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
